import { computed, ComputedRef, ref } from 'vue';
import { JointShape } from '@/modules/ConfiguratorJoints/composables/useShapeAndAngle';
import { JointPivotGlobalConfiguration } from '@/modules/ConfiguratorJoints/types/canvasTemplate';

interface CanvasPoint {
    x: number;
    y: number;
}

interface CanvasBounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

interface StageConfig {
    x?: number;
    y?: number;
    width: number;
    height: number;
}

interface ContentGroupConfig {
    x: number;
    y: number;
}

interface ArcVisualConfig {
    anchorDeg: number;
    sweepSign: number;
}

interface UseInteriorAreaParams {
    selectedShape: ComputedRef<JointShape | null>;
    angle: ComputedRef<number>;
    activeTemplate: ComputedRef<unknown | null>;
    stageConfig: ComputedRef<StageConfig | null>;
    contentGroupConfig: ComputedRef<ContentGroupConfig>;
    activePivotGlobalConfiguration: ComputedRef<JointPivotGlobalConfiguration>;
    activeArcVisualConfig: ComputedRef<ArcVisualConfig | null>;
    extensionTriangleRadialOffsetPerShape: Record<JointShape, number>;
    arcRadius: ComputedRef<number>;
    getInteriorText: () => string;
}

const interiorAreaStartPointOffsetPerShape: Record<JointShape, CanvasPoint> = {
    external: { x: 0, y: 0 },
    internal: { x: -20, y: 20 },
};

const INTERIOR_AREA_TEXT_WIDTH = 96;
const INTERIOR_AREA_TEXT_FONT_SIZE = 14;
const interiorAreaTextOffsetPerShape: Record<JointShape, CanvasPoint> = {
    external: { x: 0, y: 0 },
    internal: { x: -38, y: 90 },
};

function getPointOnRay(origin: CanvasPoint, deg: number, distance: number): CanvasPoint {
    const rad = (deg * Math.PI) / 180;
    return {
        x: origin.x + distance * Math.cos(rad),
        y: origin.y + distance * Math.sin(rad),
    };
}

function addCanvasPointOffset(point: CanvasPoint, offset: CanvasPoint): CanvasPoint {
    return {
        x: point.x + offset.x,
        y: point.y + offset.y,
    };
}

function getRayCanvasBorderPoint(origin: CanvasPoint, rayAngleDeg: number, canvasBounds: CanvasBounds): CanvasPoint {
    const rayAngleRad = (rayAngleDeg * Math.PI) / 180;
    const rayDirectionX = Math.cos(rayAngleRad);
    const rayDirectionY = Math.sin(rayAngleRad);
    const borderIntersections: CanvasPoint[] = [];

    const addIntersectionIfVisible = (distanceFromOrigin: number) => {
        if (distanceFromOrigin <= 0) {
            return;
        }

        const borderPoint = {
            x: origin.x + rayDirectionX * distanceFromOrigin,
            y: origin.y + rayDirectionY * distanceFromOrigin,
        };

        if (
            borderPoint.x >= canvasBounds.minX &&
            borderPoint.x <= canvasBounds.maxX &&
            borderPoint.y >= canvasBounds.minY &&
            borderPoint.y <= canvasBounds.maxY
        ) {
            borderIntersections.push(borderPoint);
        }
    };

    if (Math.abs(rayDirectionX) > 0.0001) {
        const distanceToLeftBorder = (canvasBounds.minX - origin.x) / rayDirectionX;
        const distanceToRightBorder = (canvasBounds.maxX - origin.x) / rayDirectionX;
        addIntersectionIfVisible(distanceToLeftBorder);
        addIntersectionIfVisible(distanceToRightBorder);
    }

    if (Math.abs(rayDirectionY) > 0.0001) {
        const distanceToTopBorder = (canvasBounds.minY - origin.y) / rayDirectionY;
        const distanceToBottomBorder = (canvasBounds.maxY - origin.y) / rayDirectionY;
        addIntersectionIfVisible(distanceToTopBorder);
        addIntersectionIfVisible(distanceToBottomBorder);
    }

    return (
        borderIntersections.sort(
            (a, b) => Math.hypot(a.x - origin.x, a.y - origin.y) - Math.hypot(b.x - origin.x, b.y - origin.y),
        )[0] ??
        getPointOnRay(
            origin,
            rayAngleDeg,
            Math.max(canvasBounds.maxX - canvasBounds.minX, canvasBounds.maxY - canvasBounds.minY),
        )
    );
}

function normalizeDeg(deg: number): number {
    return ((deg % 360) + 360) % 360;
}

function isPointInsideClockwiseWedge(
    point: CanvasPoint,
    origin: CanvasPoint,
    startDeg: number,
    endDeg: number,
): boolean {
    const pointDeg = normalizeDeg((Math.atan2(point.y - origin.y, point.x - origin.x) * 180) / Math.PI);
    const start = normalizeDeg(startDeg);
    const end = normalizeDeg(endDeg);
    const sweep = normalizeDeg(end - start);
    const pointSweep = normalizeDeg(pointDeg - start);

    return pointSweep <= sweep;
}

function getCanvasBorderCornersBetweenRays(
    origin: CanvasPoint,
    bounds: CanvasBounds,
    startDeg: number,
    endDeg: number,
): CanvasPoint[] {
    const corners = [
        { x: bounds.minX, y: bounds.minY },
        { x: bounds.maxX, y: bounds.minY },
        { x: bounds.maxX, y: bounds.maxY },
        { x: bounds.minX, y: bounds.maxY },
    ];

    return corners
        .filter((corner) => isPointInsideClockwiseWedge(corner, origin, startDeg, endDeg))
        .sort((a, b) => {
            const aSweep = normalizeDeg((Math.atan2(a.y - origin.y, a.x - origin.x) * 180) / Math.PI - startDeg);
            const bSweep = normalizeDeg((Math.atan2(b.y - origin.y, b.x - origin.x) * 180) / Math.PI - startDeg);
            return aSweep - bSweep;
        });
}

function getPolygonCenter(points: number[]): CanvasPoint {
    let area = 0;
    let centroidX = 0;
    let centroidY = 0;

    for (let index = 0; index < points.length; index += 2) {
        const nextIndex = (index + 2) % points.length;
        const x = points[index];
        const y = points[index + 1];
        const nextX = points[nextIndex];
        const nextY = points[nextIndex + 1];
        const cross = x * nextY - nextX * y;
        area += cross;
        centroidX += (x + nextX) * cross;
        centroidY += (y + nextY) * cross;
    }

    if (Math.abs(area) < 0.0001) {
        const xs = points.filter((_, index) => index % 2 === 0);
        const ys = points.filter((_, index) => index % 2 === 1);
        return {
            x: (Math.min(...xs) + Math.max(...xs)) / 2,
            y: (Math.min(...ys) + Math.max(...ys)) / 2,
        };
    }

    const signedArea = area / 2;
    return {
        x: centroidX / (6 * signedArea),
        y: centroidY / (6 * signedArea),
    };
}

export function useInteriorArea({
    selectedShape,
    angle,
    activeTemplate,
    stageConfig,
    contentGroupConfig,
    activePivotGlobalConfiguration,
    activeArcVisualConfig,
    extensionTriangleRadialOffsetPerShape,
    arcRadius,
    getInteriorText,
}: UseInteriorAreaParams) {
    function getExtensionLineApex(): CanvasPoint | null {
        if (!selectedShape.value || !activeArcVisualConfig.value) {
            return null;
        }

        const pivotConfig = activePivotGlobalConfiguration.value;
        if (!pivotConfig) {
            return null;
        }

        const { arcAnchorX, arcAnchorY } = pivotConfig;
        const { anchorDeg, sweepSign } = activeArcVisualConfig.value;
        const baseRadius = arcRadius.value + extensionTriangleRadialOffsetPerShape[selectedShape.value];
        const midDeg = anchorDeg + (sweepSign * angle.value) / 2;
        const apexDistance = baseRadius / Math.cos((angle.value * Math.PI) / 360);

        return getPointOnRay({ x: arcAnchorX, y: arcAnchorY }, midDeg, apexDistance);
    }

    const interiorAreaConfig = computed(() => {
        if (!selectedShape.value || !activeTemplate.value || !stageConfig.value) {
            return null;
        }

        const pivotConfig = activePivotGlobalConfiguration.value;
        if (!pivotConfig) {
            return null;
        }

        const { arcAnchorX, arcAnchorY } = pivotConfig;
        const contentOffset = contentGroupConfig.value;
        const visibleStageMinX = -(stageConfig.value.x ?? 0);
        const visibleStageMinY = -(stageConfig.value.y ?? 0);
        const canvasBounds = {
            minX: visibleStageMinX - contentOffset.x,
            minY: visibleStageMinY - contentOffset.y,
            maxX: visibleStageMinX + stageConfig.value.width - contentOffset.x,
            maxY: visibleStageMinY + stageConfig.value.height - contentOffset.y,
        };
        const interiorAreaStartPoint =
            selectedShape.value === 'internal' ? getExtensionLineApex() : { x: arcAnchorX, y: arcAnchorY };
        if (!interiorAreaStartPoint) {
            return null;
        }
        const origin = addCanvasPointOffset(
            interiorAreaStartPoint,
            interiorAreaStartPointOffsetPerShape[selectedShape.value],
        );

        const interiorAreaBorderRayAnglesByShape: Record<JointShape, { startDeg: number; endDeg: number }> = {
            external: {
                startDeg: angle.value,
                endDeg: 180,
            },
            internal: {
                startDeg: 0,
                endDeg: 180 + angle.value,
            },
        };
        const { startDeg, endDeg } = interiorAreaBorderRayAnglesByShape[selectedShape.value];
        const startBorderPoint = getRayCanvasBorderPoint(origin, startDeg, canvasBounds);
        const endBorderPoint = getRayCanvasBorderPoint(origin, endDeg, canvasBounds);
        const borderCorners = getCanvasBorderCornersBetweenRays(origin, canvasBounds, startDeg, endDeg);
        const points = [origin, startBorderPoint, ...borderCorners, endBorderPoint].flatMap((point) => [
            point.x,
            point.y,
        ]);

        return {
            points,
            closed: true,
            fill: 'rgba(224, 224, 224, 0.65)',
            strokeEnabled: false,
            listening: false,
        };
    });

    const interiorAreaInitialTextCenter = ref<{ shape: JointShape; center: CanvasPoint } | null>(null);

    const interiorAreaTextConfig = computed(() => {
        if (!selectedShape.value || !interiorAreaConfig.value?.points) {
            return null;
        }

        if (interiorAreaInitialTextCenter.value?.shape !== selectedShape.value) {
            interiorAreaInitialTextCenter.value = {
                shape: selectedShape.value,
                center: getPolygonCenter(interiorAreaConfig.value.points),
            };
        }

        const center = interiorAreaInitialTextCenter.value.center;
        const offset = interiorAreaTextOffsetPerShape[selectedShape.value];

        return {
            x: center.x + offset.x - INTERIOR_AREA_TEXT_WIDTH / 2,
            y: center.y + offset.y - INTERIOR_AREA_TEXT_FONT_SIZE / 2,
            width: INTERIOR_AREA_TEXT_WIDTH,
            text: getInteriorText(),
            fontSize: INTERIOR_AREA_TEXT_FONT_SIZE,
            fontStyle: 'bold',
            fill: '#757575',
            align: 'center',
            listening: false,
        };
    });

    return {
        interiorAreaConfig,
        interiorAreaTextConfig,
    };
}
